import { getApiService } from '@/services/apiService';
import { useCallback, useEffect, useState } from 'react';
import { reportAppError } from '@/helpers/sentry';

export interface MomentumData {
  signal: 'BUY' | 'WAIT' | 'HOLD' | 'UNKNOWN';
  direction: 'up' | 'down' | 'flat';
  momentum: number;       // 0-100
  decelerating: boolean;
  decelSteps: number;
  runLength: number;
  atCap: boolean;
  reason: string;
}

export interface TokenData {
  price: number;
  changePercentage: number | null;
  lastUpdated: number;
  historicalData: Array<{
    timestamp: number;
    price: number;
  }>;
  momentum?: MomentumData | null;
}

interface UseWoWTokenReturn {
  tokenData: TokenData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  lastFetchTime: number | null;
}

export const useWoWToken = (
  region: string | undefined
): UseWoWTokenReturn => {
  const api = getApiService();
  const [tokenData, setTokenData] = useState<TokenData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number | null>(null);

  const fetchTokenPrice = useCallback(async () => {
    if (!region) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/shared/data/token/${region}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: TokenData = await response.json();

      setTokenData(data);
      setLastFetchTime(Date.now());
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load token price';
      reportAppError('Failed to fetch token price', err, 'warning');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [region, api]);

  // Listen for SSE token updates
  useEffect(() => {
    const handleSSETokenUpdate = (event: CustomEvent<{
      region: string;
      price: number;
      timestamp: number;
      momentum?: MomentumData;
    }>) => {
      const { region: updatedRegion, price, timestamp, momentum } = event.detail;

      if (updatedRegion === region) {
        // Update token data with new price
        setTokenData(prev => {
          if (!prev) {
            // If we don't have previous data, fetch full data
            fetchTokenPrice();
            return prev;
          }

          // Ignore if timestamp is same or older (e.g., backend restart sending stale data)
          if (timestamp <= prev.lastUpdated) {
            // Still update momentum if provided (might be recalculated)
            if (momentum) {
              return { ...prev, momentum };
            }
            return prev;
          }

          // Calculate change percentage from previous price
          const change = price - prev.price;
          const changePercentage = Math.round(((change / prev.price) * 100) * 100) / 100;

          // Add new data point to historical data
          const newHistoricalData = [
            ...prev.historicalData,
            { timestamp, price }
          ].slice(-336); // Keep last 14 days (assuming ~24 updates/day)

          return {
            price,
            changePercentage,
            lastUpdated: timestamp,
            historicalData: newHistoricalData,
            momentum: momentum ?? prev.momentum ?? null,
          };
        });

        setLastFetchTime(Date.now());
      }
    };

    // Listen for SSE-dispatched token updates
    window.addEventListener('sseTokenUpdate', handleSSETokenUpdate as EventListener);

    // Also keep the legacy webhook listener for backwards compatibility
    const handleWebhookUpdate = (event: CustomEvent) => {
      const { region: updatedRegion } = event.detail;
      if (updatedRegion === region) {
        fetchTokenPrice();
      }
    };

    window.addEventListener('wowTokenUpdate', handleWebhookUpdate as EventListener);

    return () => {
      window.removeEventListener('sseTokenUpdate', handleSSETokenUpdate as EventListener);
      window.removeEventListener('wowTokenUpdate', handleWebhookUpdate as EventListener);
    };
  }, [region, fetchTokenPrice]);

  // Initial fetch on mount or region change
  useEffect(() => {
    if (region) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- legitimate fetch-on-mount
      fetchTokenPrice();
    }
  }, [region, fetchTokenPrice]);

  return {
    tokenData,
    loading,
    error,
    refetch: fetchTokenPrice,
    lastFetchTime
  };
};